import {
  EuiAccordion,
  EuiButtonIcon,
  EuiCallOut,
  EuiComboBox,
  EuiFieldText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiForm,
  EuiFormRow,
  EuiIcon,
  EuiText,
  EuiTextColor,
  EuiTitle,
} from "@elastic/eui";
import { useAnketaI18n } from "src/internationalization";
import { useEffect, useMemo, useState } from "react";
import { CreateNewOrganisationProps } from "src/questionnaires/types";
import {
  isValidDomain,
  OrgAttributes,
  sanitizeOrganizationAlias,
} from "../../../";
import OrgAttributesEditor from "./attributes";

export const CreateNewOrganisation = ({
  name,
  orgDomains,
  updateNewOrgs,
  removeNewOrg,
}: CreateNewOrganisationProps): JSX.Element => {
  const { translate } = useAnketaI18n();

  const [_name, setName] = useState(name);
  const alias = useMemo<string>(() => {
    return sanitizeOrganizationAlias(_name);
  }, [_name]);

  const [domains, setDomains] = useState<
    Array<{ label: string; color?: string }>
  >(orgDomains.map((org) => ({ label: org, color: "warning" })));
  const [callOut, setCallOut] = useState<JSX.Element>(<></>);

  const [attributes, setAttributes] = useState<OrgAttributes>({});

  useEffect(() => {
    const domainObject = domains.map((domain) => domain.label);
    if (updateNewOrgs !== undefined) {
      updateNewOrgs({
        alias: alias,
        domains: domainObject.length > 0 ? domainObject : [],
        name: _name,
        attributes,
      });
    }
  }, [domains, attributes]);

  const onCreateOption = (searchValue: string, flattenedOptions = []) => {
    if (!isValidDomain(searchValue)) {
      setCallOut(
        <EuiCallOut
          title={translate("anketa.create.missing.field", {
            values: {
              field: translate("anketa.organization.domains"),
            },
          })}
          color="danger"
          iconType="alert"
        />
      );
      return;
    }

    const normalizedSearchValue = searchValue.trim().toLowerCase();
    if (!normalizedSearchValue) return;
    setCallOut(<></>);

    const newOption = {
      label: searchValue.trim(),
      color: "warning",
    };

    const exists = flattenedOptions.some(
      (option) => option.label.trim().toLowerCase() === normalizedSearchValue
    );

    if (!exists) {
      setDomains((prev) => [...prev, newOption]);
    }
  };

  const buttonContent = (
    <div>
      <EuiFlexGroup gutterSize="s" alignItems="center" responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiIcon type="globe" size="m" />
        </EuiFlexItem>

        <EuiFlexItem>
          <EuiTitle size="xs">
            <h3>{name}</h3>
          </EuiTitle>
          <p>{translate("anketa.organization")}</p>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiText size="s">
        <p>
          <EuiTextColor color="subdued">
            {translate("anketa.organization.new")}
          </EuiTextColor>
        </p>
      </EuiText>
    </div>
  );

  const extraAction = (
    <EuiButtonIcon
      iconType="cross"
      color="danger"
      className="EuiAccordionForm__extraAction"
      aria-label="Delete"
      onClick={() => {
        const domainObject = domains.map((domain) => domain.label);
        removeNewOrg({
          alias: alias,
          domains: domainObject.length > 0 ? domainObject : [],
          name: _name,
        });
      }}
    />
  );
  return (
    <EuiForm>
      <EuiFormRow>{callOut}</EuiFormRow>
      <EuiFormRow>
        <EuiAccordion
          id="accordionForm1"
          className="EuiAccordionForm"
          buttonClassName="EuiAccordionForm__button"
          buttonContent={buttonContent}
          extraAction={removeNewOrg !== undefined && extraAction}
          paddingSize="l"
        >
          <EuiFlexGroup>
            <EuiFlexItem>
              <EuiFormRow label="Name" helpText={translate("anketa.required")}>
                <EuiFieldText
                  value={_name}
                  onChange={(e) => setName(e.target.value)}
                />
              </EuiFormRow>
              <EuiFormRow label="Alias" helpText={translate("anketa.required")}>
                <EuiFieldText value={alias} readOnly />
              </EuiFormRow>
            </EuiFlexItem>
            <EuiFlexItem>
              <EuiFormRow
                label={translate("anketa.organization.domains")}
                helpText={translate("anketa.organization.domain.helper")}
              >
                <EuiComboBox
                  noSuggestions
                  selectedOptions={domains}
                  onCreateOption={onCreateOption}
                  onChange={(selected) => {
                    setDomains(
                      selected.map((option) => ({
                        label: option.label,
                        color: option.color ?? "primary",
                      }))
                    );
                  }}
                  placeholder="Add domains"
                  isDisabled={updateNewOrgs === undefined}
                />
              </EuiFormRow>
            </EuiFlexItem>
          </EuiFlexGroup>
          <OrgAttributesEditor
            initialAttributes={{
              isPartner: ["true"],
            }}
            onChange={setAttributes}
          />
        </EuiAccordion>
      </EuiFormRow>
    </EuiForm>
  );
};
